/**
 * Revisión de una propuesta de IA, **pieza a pieza**.
 *
 * Aquí ocurre lo que hace segura toda la feature: cada pieza pasa por
 * `validateProposalItem`, que cruza los **dos** registros del repo
 * (`BlockPlugin.validate` para la definición de un bloque y
 * `CourseModulePlugin.configSchema` para la config de un módulo — no son lo
 * mismo ni viven en el mismo sitio). Lo que no valida sale en rojo y **no se
 * puede marcar**: la casilla está deshabilitada, no solo advertida.
 *
 * Aplicar reusa `applyApprovedItems`, que llama a las mutaciones de siempre. Por
 * eso el botón «Deshacer» de arriba es el `useUndo` normal del editor y no hubo
 * que escribirlo.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, Check, Sparkles, Undo2, X } from "lucide-solid";
import {
  type AiProposalItem,
  applyApprovedItems,
  summarizeReview,
  validateProposalItems,
} from "@space/shared";
import { defaultRegistry } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { moduleRegistry } from "@/features/academy/modules/registry";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { useCourseWrite } from "@/features/courses/hooks/use-course-data";
import { useAiProposal, useDecideItems } from "./api";

const blockRegistry = defaultRegistry();

export const AiProposalReview: Component<{
  proposalId: string;
  courseId?: string;
  /** Lección destino de las piezas `block` (propuesta de contenido). */
  lessonId?: string;
  onClose?: () => void;
}> = (props) => {
  const { t } = useI18n();
  const proposal = useAiProposal(() => props.proposalId);
  const settings = useCourseSettings(() => props.courseId);
  const write = useCourseWrite();
  const decide = useDecideItems();
  const [approved, setApproved] = createSignal<ReadonlySet<string>>(new Set());
  const [applying, setApplying] = createSignal(false);

  const enabledKinds = createMemo(() =>
    moduleRegistry.enabledKinds(settings.data?.enabled_module_kinds),
  );

  const items = createMemo<AiProposalItem[]>(() => proposal.data?.items ?? []);

  /** El veredicto. Se recalcula solo si cambian las piezas o los kinds. */
  const verdicts = createMemo(() =>
    validateProposalItems(items(), {
      blocks: blockRegistry,
      modules: moduleRegistry,
      enabledModuleKinds: enabledKinds(),
    }),
  );

  const summary = createMemo(() => summarizeReview(items(), verdicts()));
  // Lingui exige variables simples en las interpolaciones (una expresión no se
  // puede traducir con contexto): se desglosan aquí.
  const validCount = () => summary().valid;
  const totalCount = () => summary().total;
  const appliedCount = () => summary().applied;
  const selectedCount = () => approved().size;

  const toggle = (id: string, on: boolean) => {
    setApproved((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const selectAllValid = () => {
    setApproved(
      new Set(
        items()
          .filter((i) => verdicts().get(i.id)?.valid && i.status === "pending")
          .map((i) => i.id),
      ),
    );
  };

  const reject = async (item: AiProposalItem) => {
    await decide.mutateAsync({
      proposalId: props.proposalId,
      decisions: [{ itemId: item.id, status: "rejected" }],
    });
    await proposal.refetch();
  };

  /** Curso destino. Accessor y no `const`: desestructurar `props` en Solid
   * congela el valor del primer render — y además `oxlint --fix` del pre-commit
   * reescribe cualquier `const x = props.x` a un destructuring, con lo que el
   * arreglo se deshacía solo en cada commit. Un accessor no le da esa forma. */
  const targetCourseId = () => props.courseId ?? "";

  const apply = async () => {
    const db = write.drizzle();
    const courseId = targetCourseId();
    if (!db || !courseId) {
      showToast(t(msg`Open the course editor to apply this proposal.`), "error");
      return;
    }
    setApplying(true);
    try {
      // Las mutaciones son las MISMAS del editor manual: actividad, papelera y
      // deshacer funcionan sin código propio.
      const result = await applyApprovedItems(items(), verdicts(), approved(), {
        db,
        courseId,
        defaultLessonId: props.lessonId,
      });
      await decide.mutateAsync({ proposalId: props.proposalId, decisions: result.decisions });
      await proposal.refetch();
      setApproved(new Set<string>());
      if (result.failures.length > 0) {
        showToast(t(msg`Some pieces could not be applied`), "error");
      } else {
        showToast(t(msg`Proposal applied`), "success");
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : t(msg`Could not apply the proposal`),
        "error",
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <section class="space-y-4" data-testid="ai-proposal-review">
      <Show when={proposal.isLoading}>
        <Skeleton class="h-40 w-full" />
      </Show>
      <Show when={proposal.data}>
        {(detail) => (
          <>
            <header class="rounded-xl border border-border/40 bg-muted/30 p-4">
              <div class="flex items-start gap-2">
                <Sparkles class="mt-0.5 h-4 w-4 text-accent" />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium">{detail().prompt}</p>
                  <p class="mt-1 text-xs text-muted-foreground">
                    {(() => {
                      const valid = validCount();
                      const total = totalCount();
                      const applied = appliedCount();
                      return t(
                        msg`${valid} of ${total} pieces can be approved · ${applied} already applied`,
                      );
                    })()}
                  </p>
                </div>
              </div>
              {/* La mitad visible de la regla del híbrido: si el proveedor no
                  garantizó el esquema, la revisión merece más atención. */}
              <Show when={!detail().schemaEnforced}>
                <p
                  class="mt-3 flex items-start gap-2 rounded-lg bg-warning/10 p-2 text-xs text-warning-foreground"
                  data-testid="ai-proposal-unenforced"
                >
                  <AlertTriangle class="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {t(
                    msg`This model does not guarantee the output format, so the server had to repair it. Read every piece carefully before approving.`,
                  )}
                </p>
              </Show>
            </header>

            <div class="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllValid}
                data-testid="ai-select-all-valid"
              >
                {t(msg`Select everything valid`)}
              </Button>
              <Button
                size="sm"
                disabled={approved().size === 0 || applying()}
                onClick={() => void apply()}
                data-testid="ai-apply-selected"
              >
                <Check class="mr-1 h-4 w-4" />
                {(() => {
                  const selected = selectedCount();
                  return t(msg`Apply selected (${selected})`);
                })()}
              </Button>
              <Show when={props.onClose}>
                <Button variant="ghost" size="sm" onClick={() => props.onClose?.()}>
                  {t(msg`Close`)}
                </Button>
              </Show>
            </div>

            <ul class="space-y-2" data-testid="ai-proposal-items">
              <For each={items()}>
                {(item) => {
                  const verdict = () => verdicts().get(item.id);
                  const decided = () => item.status === "applied" || item.status === "rejected";
                  return (
                    <li
                      class="rounded-lg border border-border/40 p-3"
                      classList={{
                        "border-destructive/40 bg-destructive/5": !verdict()?.valid,
                        "opacity-60": decided(),
                      }}
                      data-testid="ai-proposal-item"
                      data-valid={verdict()?.valid ? "true" : "false"}
                      data-status={item.status}
                    >
                      <div class="flex items-start gap-3">
                        <Checkbox
                          checked={approved().has(item.id)}
                          // La barrera, no un aviso: lo inválido no se puede marcar.
                          disabled={!verdict()?.valid || decided()}
                          onChange={(on: boolean) => {
                            toggle(item.id, on);
                          }}
                          data-testid="ai-item-approve"
                        />
                        <div class="min-w-0 flex-1">
                          <p class="truncate text-sm font-medium">
                            {item.title ?? `${item.op} ${item.entity}`}
                          </p>
                          <p class="text-xs text-muted-foreground">
                            {item.op} · {item.entity} · {item.path}
                          </p>
                          <Show when={(verdict()?.errors.length ?? 0) > 0}>
                            <ul class="mt-1 space-y-0.5" data-testid="ai-item-errors">
                              <For each={verdict()?.errors}>
                                {(e) => <li class="text-xs text-destructive">{e}</li>}
                              </For>
                            </ul>
                          </Show>
                        </div>
                        <Show when={!decided()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void reject(item)}
                            data-testid="ai-item-reject"
                            aria-label={t(msg`Reject this piece`)}
                          >
                            <X class="h-4 w-4" />
                          </Button>
                        </Show>
                        <Show when={item.status === "applied"}>
                          <span class="flex items-center gap-1 text-xs text-success">
                            <Undo2 class="h-3.5 w-3.5" />
                            {t(msg`Applied`)}
                          </span>
                        </Show>
                      </div>
                    </li>
                  );
                }}
              </For>
            </ul>
          </>
        )}
      </Show>
    </section>
  );
};
